<template>
  <li
    v-if="hasEnoughData || $isPageEditing"
    :class="['accordion-block__item', { 'accordion-block__item--expanded': isOpen }]"
  >
    <button
      :id="`accordion-item-control-${uid}`"
      :aria-controls="`accordion-item-content-${uid}`"
      class="accordion-block__item-btn"
      :aria-expanded="isOpen"
      @click="toggleOpen"
    >
      <sc-text
        :field="fields.title"
        :tag="headingType"
        class="accordion-block__item-txt font-s"
        v-on="$isPageEditing ? { keyup: onKeyup } : {}"
      />
      <i class="accordion-block__item-icon icon-Down" aria-hidden="true"></i>
    </button>

    <div
      :id="`accordion-item-content-${uid}`"
      ref="accordionNode"
      class="accordion-block__item-content"
      :aria-hidden="!isOpen"
    >
      <sc-rich-text
        v-track-rich-text-links="'accordion_block'"
        :field="fields.content"
        class="rich-text"
      />
      <slot />
    </div>
  </li>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { Text as ScText, RichText as ScRichText } from '@sitecore-jss/sitecore-jss-vue';
import useId from '../../../composables/useId';
import vTrackRichTextLinks from '../../../directives/vTrackRichTextLinks';
import { useAccordionTracking } from '../../../data-layer/hooks/useAccordionTracking';
import type { AccordionBlockItemProps } from '../types';

const props = withDefaults(defineProps<AccordionBlockItemProps>(), {
  fields: () => ({}),
  params: () => ({}),
  external: false,
  open: false,
  headingType: 'h3',
});

const emit = defineEmits<{
  accordionClick: [isOpen: boolean];
}>();

const isOpen = ref(props.open);
const accordionNode = ref<HTMLElement | null>(null);
const uid = ref(useId());
const previousExpandedHeightStyle = ref<string | null>(null);
let observer: ResizeObserver | null = null;

const { trackAccordionState } = useAccordionTracking({
  linkText: props.fields?.title?.value,
});

const hasEnoughData = computed(
  () =>
    (!!props.rendering && 'dataSource' in props.rendering && props.rendering.dataSource) ||
    props.external
);

const applyAcordionExpandedStyles = (node: HTMLElement | null) => {
  const accordionExpandedHeight = node?.scrollHeight;

  if (accordionExpandedHeight) {
    const accordionExpandedHeightStyle = Math.ceil(accordionExpandedHeight / 50) * 50 + 'px';

    if (accordionExpandedHeightStyle !== previousExpandedHeightStyle.value) {
      if (node) {
        node.style.cssText = `--accordion-expanded-height: ${accordionExpandedHeightStyle};`;
      }
      previousExpandedHeightStyle.value = accordionExpandedHeightStyle;
    }
  }
};

onMounted(() => {
  if (!hasEnoughData.value) {
    return;
  }

  observer = new ResizeObserver(() => applyAcordionExpandedStyles(accordionNode.value));

  accordionNode.value?.querySelectorAll(':not(div)').forEach((childNode) => {
    observer?.observe(childNode);
  });
});

onUnmounted(() => {
  observer?.disconnect();
});

const toggleOpen = () => {
  isOpen.value = !isOpen.value;

  emit('accordionClick', isOpen.value);

  trackAccordionState(isOpen.value);
};

const onKeyup = (event: KeyboardEvent) => {
  if (event.code === 'Space') {
    event.preventDefault();
  }
};

defineExpose({ accordionNode });
</script>

<style lang="scss">
@import './scss/AccordionBlockItem.scss';
</style>
